import * as React from 'react';
import styled from 'styled-components';
import Skeleton from '@material-ui/lab/Skeleton';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Chip from '@material-ui/core/Chip';
import {GuidelineIcon} from '@epic-ui/icons';
import {InfoBlock, InfoBlockProps} from './InfoBlock';
import {Tooltip} from '../../../components';
import useWidth from '../../v2/sections/fortnite-games/components/chart/useWidth';

import {Status} from '../../../types';

const MIN_TITLE_WIDTH = 350;

const getAttribute = (attr: string | number): string =>
    typeof attr === 'number' ? `${attr}px` : attr;

export const ContentSkeleton = styled(Skeleton)`
    border-radius: ${p => p.theme.muiTheme.shape.borderRadius}px;
`;

const WidgetHeaderWrapper = styled.div<{headerAlign?: string}>`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    min-height: 50px;
    padding: ${p => p.theme.muiTheme.spacing(0, 0, 3, 0)};
    transition: background 1s linear;
    user-select: none;

    align-self: ${p => p.headerAlign};

    & h5 {
        font-size: 0.875rem;
        font-weight: 400;
        height: 1.25rem;

        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        color: ${p => p.theme.muiTheme.palette.text.primary};
    }

    ${p => p.theme.muiTheme.breakpoints.up('flg')} {
        & h5 {
            font-size: 1.12rem;
            font-weight: 300;
            height: 1.334em;
        }
    }

    ${p => p.theme.muiTheme.breakpoints.up('xl')} {
        & h5 {
            font-size: 1.38rem;
        }
    }
`;

interface WidgetContentWrapperProps {
    height: string | number;
    flex?: string | number;
    padding?: string | number;
    /** Specifies overflow for WidgetContent, defaults to `hidden`. */
    overflow?: string;
}

export const WidgetContentWrapper = styled(Card)<WidgetContentWrapperProps>`
    display: flex;
    flex-wrap: wrap;

    flex: ${p => p.flex || 1};
    height: ${p => (p.height ? getAttribute(p.height) : '100%')};
    min-height: ${p => (p.height ? getAttribute(p.height) : '100%')};
    padding: ${p => p.theme.muiTheme.spacing(3)}px;
    border-radius: ${p => p.theme.muiTheme.shape.borderRadius}px;
    color: ${p => p.theme.muiTheme.palette.text.primary};
    background-color: ${p => p.theme.muiTheme.palette.background.paper};
    overflow: ${p => p.overflow || 'hidden'} !important;

    ${p => p.theme.muiTheme.breakpoints.up('fmd')} {
        padding: ${p => p.padding};
    }
`;

const WidgetActions = styled(Box)`
    display: flex;
    justify-content: flex-end;
    align-content: center;

    & > * {
        margin-left: ${p => p.theme.muiTheme.spacing(2)}px;
    }
`;

const WidgetTitle = styled.div<{titleWidth: string}>`
    display: flex;
    overflow: hidden;
    align-items: center;
    min-height: 40px;

    max-width: ${p => p.titleWidth};
`;

interface WidgetHeaderProps {
    header?: React.ReactNode;
    headerAdornment?: React.ReactNode;
    headerAlign?: string;
    headerContent?: React.ReactNode;
    actions?: React.ReactNode;
    tooltip?: React.ReactNode;
    label?: string;
}

const WidgetHeader: React.FC<WidgetHeaderProps> = ({
    header,
    headerAdornment,
    headerContent,
    actions,
    headerAlign,
    tooltip,
    label
}: WidgetHeaderProps) => {
    const [headerContainerRef, headerWidth] = useWidth();
    const [actionsContainerRef, actionsWidth] = useWidth();

    const titleWidth = headerContent
        ? '100%'
        : `${headerWidth - actionsWidth || MIN_TITLE_WIDTH}px`;

    return (
        <WidgetHeaderWrapper headerAlign={headerAlign} ref={headerContainerRef}>
            <div>
                <WidgetTitle titleWidth={titleWidth}>
                    <Typography noWrap variant="h5" color="textPrimary">
                        {header}
                    </Typography>
                    {headerAdornment}
                    {tooltip && (
                        <Tooltip placement="top" title={tooltip}>
                            <Box mx={2} display="flex" alignItems="center">
                                <GuidelineIcon />
                            </Box>
                        </Tooltip>
                    )}
                </WidgetTitle>
                {headerContent}
            </div>

            {label && <Chip label={label} />}
            {actions && <WidgetActions ref={actionsContainerRef}>{actions}</WidgetActions>}
        </WidgetHeaderWrapper>
    );
};

interface WidgetContentProps extends WidgetContentWrapperProps, InfoBlockProps {
    skeleton?: React.ReactElement;
    children?: React.ReactNode;
}

const WidgetContent: React.FC<WidgetContentProps> = ({
    status = Status.SUCCESS,
    height,
    padding,
    overflow,
    message,
    children,
    skeleton,
    retry,
    docsUrl,
    ...rest
}: WidgetContentProps) => {
    return (
        <WidgetContentWrapper
            height={height}
            padding={status === Status.SUCCESS ? padding : 0}
            overflow={overflow}
            {...rest}>
            {status === Status.REQUEST && (skeleton || children)}
            {status === Status.SUCCESS && children}
            {status !== Status.REQUEST && message && (
                <InfoBlock message={message} status={status} retry={retry} docsUrl={docsUrl} />
            )}
        </WidgetContentWrapper>
    );
};

interface Props extends WidgetContentProps, WidgetHeaderProps {
    /** Content to put after header on success. */
    options?: React.ReactNode;
    /** Grid area. */
    gridArea?: string;
    skeleton?: React.ReactElement;
    width?: string | number;
    minWidth?: string | number;
}

const defaultSkeleton = <ContentSkeleton variant="rect" width="100%" height={300} />;

interface State {
    hasError: boolean;
}

class Widget extends React.Component<Props, State> {
    static defaultProps = {
        status: Status.SUCCESS,
        message: '',
        overflow: 'visible',
        skeleton: defaultSkeleton,
        minWidth: 334
    };

    static Header = WidgetHeader;
    static Content = WidgetContent;
    static ContentSkeleton = ContentSkeleton;

    state = {
        hasError: false
    };

    componentDidCatch(): void {
        this.setState({hasError: true});
    }

    render(): JSX.Element {
        const {hasError} = this.state;
        const {
            header,
            headerAdornment,
            headerContent,
            headerAlign,
            actions,
            options,
            tooltip,
            label,
            gridArea,
            width,
            minWidth,
            status,
            children,
            ...rest
        } = this.props;

        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="stretch"
                height="100%"
                position="relative"
                width={width || '100%'}
                minWidth={minWidth}
                gridArea={gridArea}>
                <WidgetHeader
                    header={header}
                    headerAdornment={headerAdornment}
                    actions={actions}
                    headerContent={headerContent}
                    tooltip={tooltip}
                    label={label}
                    headerAlign={headerAlign}
                />
                {options && <Box display="flex">{options}</Box>}
                <WidgetContent status={hasError ? Status.ERROR : status} {...rest}>
                    {children}
                </WidgetContent>
            </Box>
        );
    }
}

export default Widget;
